// NEXUS Sound Engine — Web Audio API synthesis (no network, no 403s, always works)
// Each sticker tier gets a distinct procedurally-generated sound effect.

let ctx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch { ctx = null; }
  }
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// Helper — schedule oscillator burst
function tone({ freq = 440, type = "sine", dur = 0.18, when = 0, gain = 0.18, slideTo = null, q = 1 }) {
  const c = getCtx(); if (!c) return;
  const t0 = c.currentTime + when;
  const osc = c.createOscillator();
  const g = c.createGain();
  const filt = c.createBiquadFilter();
  filt.type = "lowpass"; filt.frequency.value = 8000; filt.Q.value = q;
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(filt); filt.connect(g); g.connect(c.destination);
  osc.start(t0); osc.stop(t0 + dur + 0.05);
}

// Noise burst — for explosions / shockwaves
function noise({ dur = 0.3, when = 0, gain = 0.2, lowpass = 1200, highpass = 100 }) {
  const c = getCtx(); if (!c) return;
  const t0 = c.currentTime + when;
  const buf = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const src = c.createBufferSource(); src.buffer = buf;
  const hp = c.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = highpass;
  const lp = c.createBiquadFilter(); lp.type = "lowpass";  lp.frequency.value = lowpass;
  const g = c.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(c.destination);
  src.start(t0); src.stop(t0 + dur + 0.05);
}

// === Per-animation sound profiles ===
const PLAYERS = {
  // Free
  "float-spin":   () => { tone({freq: 880,  dur: 0.12}); tone({freq: 1320, dur: 0.18, when: 0.08}); },
  "ripple":       () => { tone({freq: 660,  dur: 0.18, slideTo: 880}); tone({freq: 1320, dur: 0.14, when: 0.1}); },
  "heart-rise":   () => { tone({freq: 523,  dur: 0.16}); tone({freq: 698,  dur: 0.2,  when: 0.12}); },
  "wave-side":    () => { tone({freq: 220,  type:"sine", dur: 0.4, slideTo: 660, gain: 0.15}); },
  "bubble-rise":  () => { for (let i = 0; i < 4; i++) tone({freq: 400 + i*180, dur: 0.08, when: i*0.06}); },
  "twinkle":      () => { [880, 1320, 1760, 2349].forEach((f, i) => tone({freq: f, dur: 0.07, when: i*0.05})); },
  // Paid
  "zap-flash":    () => { tone({freq: 1200, type:"square", dur: 0.08}); tone({freq: 2400, type:"square", dur: 0.06, when: 0.04}); noise({dur: 0.08, when: 0.02, gain: 0.1}); },
  "flame-up":     () => { noise({dur: 0.4, gain: 0.18, lowpass: 600, highpass: 200}); tone({freq: 220, dur: 0.3, slideTo: 110, gain: 0.1}); },
  "confetti-rain":() => { for (let i = 0; i < 8; i++) tone({freq: 800 + Math.random()*1200, type:"triangle", dur: 0.08, when: i*0.04, gain: 0.1}); },
  "money-fall":   () => { [1568, 1318, 1046, 1568].forEach((f, i) => tone({freq: f, type:"triangle", dur: 0.1, when: i*0.07, gain: 0.13})); },
  "flower-fall":  () => { [659, 784, 988, 1175].forEach((f, i) => tone({freq: f, type:"sine", dur: 0.18, when: i*0.08, gain: 0.12})); },
  "crown-bounce": () => { [392, 523, 659, 784, 988].forEach((f, i) => tone({freq: f, type:"triangle", dur: 0.14, when: i*0.06, gain: 0.14})); },
  "rocket-launch":() => { tone({freq: 80,  dur: 0.6, slideTo: 400, type:"sawtooth", gain: 0.18}); noise({dur: 0.6, gain: 0.1, lowpass: 800}); },
  "diamond-rain": () => { for (let i = 0; i < 6; i++) tone({freq: 1500 + i*250, type:"triangle", dur: 0.1, when: i*0.05, gain: 0.1}); },
  "rainbow-arc":  () => { [523, 587, 659, 698, 784, 880, 988].forEach((f, i) => tone({freq: f, dur: 0.1, when: i*0.05, gain: 0.12})); },
  // Premium
  "thunder-strike":() => { noise({dur: 0.5, gain: 0.32, lowpass: 400, highpass: 50}); tone({freq: 100, dur: 0.4, slideTo: 40, type:"sawtooth", gain: 0.2}); },
  "butterfly-orbit":() => { [659, 523, 698, 523, 784].forEach((f, i) => tone({freq: f, type:"sine", dur: 0.16, when: i*0.1, gain: 0.13})); },
  "sakura-fall":  () => { [880, 1108, 1318, 1108, 880].forEach((f, i) => tone({freq: f, type:"sine", dur: 0.18, when: i*0.12, gain: 0.11})); },
  "skull-spin":   () => { tone({freq: 200, type:"square", dur: 0.3, slideTo: 80, gain: 0.18}); noise({dur: 0.4, gain: 0.12, lowpass: 800, highpass: 150}); },
  "dragon-trail": () => { tone({freq: 110, type:"sawtooth", dur: 0.8, slideTo: 55, gain: 0.22}); noise({dur: 0.6, when: 0.2, gain: 0.15, lowpass: 700}); },
  "unicorn-prism":() => { [523, 659, 784, 988, 1175, 1318].forEach((f, i) => tone({freq: f, type:"triangle", dur: 0.14, when: i*0.07, gain: 0.13})); },
  "comet-streak": () => { tone({freq: 220, dur: 0.5, slideTo: 1760, type:"sawtooth", gain: 0.2}); noise({dur: 0.4, when: 0.1, gain: 0.1, lowpass: 2000}); },
  // Elite
  "phoenix-rise": () => { tone({freq: 220, dur: 0.8, slideTo: 880, type:"sawtooth", gain: 0.22}); [523, 659, 784, 988, 1175].forEach((f, i) => tone({freq: f, dur: 0.15, when: 0.3 + i*0.08, gain: 0.15})); },
  "sigil-warp":   () => { for (let i = 0; i < 5; i++) tone({freq: 100 * Math.pow(1.5, i), dur: 0.2, when: i*0.08, type:"sawtooth", gain: 0.15}); },
  // Mega — full spectacle
  "explode":      () => { noise({dur: 1.0, gain: 0.4, lowpass: 800, highpass: 30}); tone({freq: 60, dur: 0.6, type:"sawtooth", gain: 0.25}); tone({freq: 200, dur: 0.4, slideTo: 50, type:"square", gain: 0.2, when: 0.1}); },
  "shockwave":    () => { tone({freq: 1500, type:"sine", dur: 0.05, gain: 0.25}); noise({dur: 1.2, gain: 0.35, lowpass: 600, highpass: 40}); tone({freq: 80, dur: 0.8, slideTo: 30, type:"sawtooth", gain: 0.22, when: 0.05}); },
  "supernova":    () => { noise({dur: 1.5, gain: 0.4, lowpass: 1200, highpass: 50}); [220, 440, 880, 1760, 3520].forEach((f, i) => tone({freq: f, dur: 0.4, when: i*0.15, type:"sine", gain: 0.15})); },
  "kraken-emerge":() => { tone({freq: 50,  dur: 1.4, slideTo: 30, type:"sawtooth", gain: 0.3}); noise({dur: 1.4, gain: 0.25, lowpass: 400, highpass: 30}); [110, 165, 220].forEach((f, i) => tone({freq: f, dur: 0.5, when: 0.3 + i*0.2, type:"sawtooth", gain: 0.18})); },
  "cosmic-swirl": () => { for (let i = 0; i < 10; i++) tone({freq: 200 * Math.pow(1.4, i % 6), dur: 0.3, when: i*0.12, type: i % 2 ? "sine" : "triangle", gain: 0.14}); noise({dur: 2, gain: 0.18, lowpass: 1500, highpass: 100}); },
};

export function playStickerSound(animation) {
  try {
    getCtx();   // ensure context active
    const player = PLAYERS[animation] || PLAYERS["float-spin"];
    player();
  } catch (e) { /* swallow */ }
}

// Unlock audio on first user gesture (browsers require this)
export function primeAudioContext() {
  const c = getCtx();
  if (c && c.state === "suspended") c.resume().catch(() => {});
}

// Not `{ once: true }`: mobile browsers re-suspend the AudioContext after
// backgrounding/screen-lock, so the unlock needs to survive past the first
// gesture to catch later re-suspensions too (see lib/ringAudio.js).
if (typeof window !== "undefined") {
  ["click", "touchstart", "keydown"].forEach((e) =>
    window.addEventListener(e, primeAudioContext, { passive: true }));
}
